/**
 * src/components/auth/SignUpForm.tsx
 * ------------------------------------
 * Registration form embedded in SignInModal (sign_up tab).
 *
 * Fully implemented this will:
 *   - Collect: displayName, email, password, passwordConfirm.
 *   - Client-side validation: email format, password min 8 chars,
 *     passwords match.
 *   - Call signUp() from src/api/auth.ts on submit.
 *   - On success: auto-sign-in, merge anon session, call onSuccess().
 *   - Show inline field-level errors.
 *   - Accept Terms of Service checkbox (links to org's ToS URL).
 *
 * Props:
 *   onSuccess — () => void (called after successful sign-up + sign-in)
 *
 * Key dependencies:
 *   src/api/auth.ts, src/stores/auth.ts,
 *   src/components/ui/Button.tsx, src/components/ui/Input.tsx
 */
import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useAuth } from '@/hooks/useAuth'

interface SignUpFormProps {
  onSuccess: () => void
}

export function SignUpForm({ onSuccess }: SignUpFormProps) {
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { signUp } = useAuth()

  const validate = (): string | null => {
    if (displayName.trim().length < 2) return 'Display name must be at least 2 characters.'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email address.'
    if (password.length < 8) return 'Password must be at least 8 characters.'
    if (password !== passwordConfirm) return 'Passwords do not match.'
    if (!agreed) return 'You must accept the Terms of Service.'
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const validationError = validate()
    if (validationError) { setError(validationError); return }
    setError('')
    setLoading(true)
    try {
      await signUp(email, password, displayName)
      onSuccess()
    } catch {
      setError('Could not create account. The email may already be in use.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <Input
        label="Display name"
        type="text"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        autoComplete="name"
        required
      />
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
        required
      />
      <Input
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
        required
      />
      <Input
        label="Confirm password"
        type="password"
        value={passwordConfirm}
        onChange={(e) => setPasswordConfirm(e.target.value)}
        autoComplete="new-password"
        required
      />

      <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5 rounded"
        />
        I agree to the{' '}
        <a href="/terms" target="_blank" className="text-primary-600 underline">
          Terms of Service
        </a>
      </label>

      {error && (
        <p role="alert" className="text-xs text-red-500">
          {error}
        </p>
      )}

      <Button type="submit" disabled={loading} className="w-full">
        {loading ? 'Creating account…' : 'Create Account'}
      </Button>
    </form>
  )
}
